import { logDebug } from '../utils/logger';
import { getCached, setCached, CACHE_TTL } from '../utils/cache';
import { getDeviceType } from '../utils/deviceDetection';

const GOOGLE_SCRIPT_URL = (process.env.REACT_APP_GOOGLE_SCRIPT_URL || '').trim();
const API_KEY = (process.env.REACT_APP_API_KEY || 'default-api-key-change-me').trim();
const CLIENT_CHANNEL_OVERRIDE = (process.env.REACT_APP_ATTENDANCE_CLIENT_CHANNEL || '').trim().toLowerCase();
const DEFAULT_EMPLOYEE_LOGIN_PASSWORD = (process.env.REACT_APP_EMPLOYEE_LOGIN_PASSWORD || '').trim();
const AUTH_SESSION_STORAGE_KEY = 'chamcong_auth_session_v1';
const AUTH_REFRESH_SKEW_SECONDS = 20;

const resolveAttendanceClientChannel = (deviceType) => {
  if (CLIENT_CHANNEL_OVERRIDE) return CLIENT_CHANNEL_OVERRIDE;
  return String(deviceType || '').toLowerCase() === 'mobile' ? 'mobile_web_app' : 'web_portal';
};

const appendApiKey = (params) => {
  if (API_KEY) {
    params.append('api_key', API_KEY);
  }
};

const parseJsonResponse = async (response, fallbackErrorMessage) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`${fallbackErrorMessage}: ${text.substring(0, 200)}`);
  }
};

const normalizeEmployeeCode = (value) => String(value || '').trim().toLowerCase();
const normalizeEmployeeEmail = (value) => String(value || '').trim().toLowerCase();
const normalizeEmployeeId = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const readAuthSessionMap = () => {
  try {
    const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeAuthSessionMap = (mapValue) => {
  try {
    localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(mapValue || {}));
  } catch {
    // ignore localStorage write failure
  }
};

const getAuthSessionByCode = (employeeCode) => {
  const code = normalizeEmployeeCode(employeeCode);
  if (!code) return null;
  const mapValue = readAuthSessionMap();
  return mapValue[code] || null;
};

const setAuthSessionByCode = (employeeCode, session) => {
  const code = normalizeEmployeeCode(employeeCode);
  if (!code || !session || !session.auth_token) return;
  const mapValue = readAuthSessionMap();
  mapValue[code] = session;
  writeAuthSessionMap(mapValue);
};

const clearAuthSessionByCode = (employeeCode) => {
  const code = normalizeEmployeeCode(employeeCode);
  if (!code) return;
  const mapValue = readAuthSessionMap();
  if (!Object.prototype.hasOwnProperty.call(mapValue, code)) return;
  delete mapValue[code];
  writeAuthSessionMap(mapValue);
};

const isAuthSessionUsable = (session) => {
  if (!session || !session.auth_token) return false;
  const exp = Number(session.expires_at || 0);
  if (!Number.isFinite(exp) || exp <= 0) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return exp - nowSec > AUTH_REFRESH_SKEW_SECONDS;
};

const generateNonce = (length = 24) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const size = Math.min(128, Math.max(8, Number(length) || 24));
  let output = '';

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const values = new Uint8Array(size);
    crypto.getRandomValues(values);
    for (let i = 0; i < size; i += 1) {
      output += chars.charAt(values[i] % chars.length);
    }
    return output;
  }

  for (let i = 0; i < size; i += 1) {
    output += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return output;
};

const getLocalEmployeeSnapshot = () => {
  try {
    const raw = localStorage.getItem('chamcong_employee');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const resolveEmployeeIdForAuth = (data) => {
  const fromPayload = normalizeEmployeeId(
    data?.employee_id ?? data?.employeeId ?? data?.id ?? ''
  );
  if (fromPayload) return fromPayload;

  const fromStorage = getLocalEmployeeSnapshot();
  return normalizeEmployeeId(
    fromStorage?.id ??
    fromStorage?.employee_id ??
    fromStorage?.employeeId ??
    ''
  );
};

const resolveEmployeeEmailForAuth = (data) => {
  const fromPayload = normalizeEmployeeEmail(
    data?.email ??
    data?.employee_email ??
    ''
  );
  if (fromPayload) return fromPayload;

  const fromStorage = getLocalEmployeeSnapshot();
  return normalizeEmployeeEmail(
    fromStorage?.email ??
    fromStorage?.employee_email ??
    ''
  );
};

const resolveEmployeeCodeForAuth = (data) => {
  const fromPayload = normalizeEmployeeCode(data?.employee_code || data?.employeeCode || '');
  if (fromPayload) return fromPayload;

  const fromStorage = getLocalEmployeeSnapshot();
  return normalizeEmployeeCode(
    fromStorage?.code || fromStorage?.employee_code || fromStorage?.employeeCode || ''
  );
};

const fetchEmployeesForAuth = async () => {
  const params = new URLSearchParams();
  params.append('action', 'employees');
  appendApiKey(params);

  const response = await fetch(`${GOOGLE_SCRIPT_URL}?${params.toString()}`, {
    method: 'GET',
    cache: 'no-store',
  });
  const payload = await parseJsonResponse(response, 'Khong doc duoc danh sach nhan vien');

  if (!response.ok) {
    throw new Error(payload?.error || `HTTP ${response.status}`);
  }

  if (!payload || !payload.ok || !Array.isArray(payload.data)) {
    throw new Error(payload?.error || 'Khong lay duoc danh sach nhan vien');
  }

  return payload.data;
};

const resolveEmployeeCodeFromEmployees = async (data) => {
  const targetId = resolveEmployeeIdForAuth(data);
  const targetEmail = resolveEmployeeEmailForAuth(data);
  if (!targetId && !targetEmail) return '';

  const employees = await fetchEmployeesForAuth();
  const matched = employees.find((emp) => {
    if (!emp) return false;
    const empCode = normalizeEmployeeCode(emp.code || emp.employee_code || '');
    if (!empCode) return false;

    const empId = normalizeEmployeeId(emp.id ?? emp.employee_id ?? emp.employeeId ?? '');
    if (targetId && empId && empId === targetId) return true;

    const empEmail = normalizeEmployeeEmail(emp.email || emp.employee_email || '');
    if (targetEmail && empEmail && empEmail === targetEmail) return true;

    return false;
  });

  return normalizeEmployeeCode(matched?.code || matched?.employee_code || '');
};

const requestLoginAuthSession = async (employeeCode, password = '') => {
  const params = new URLSearchParams();
  params.append('action', 'login');
  params.append('employee_code', employeeCode);
  if (String(password || '').trim()) {
    params.append('password', String(password || ''));
  }
  appendApiKey(params);

  const response = await fetch(GOOGLE_SCRIPT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });
  const loginResult = await parseJsonResponse(response, 'Khong doc duoc phan hoi dang nhap');

  if (!response.ok) {
    throw new Error(loginResult.error || `HTTP ${response.status}`);
  }

  if (!loginResult || !loginResult.ok) {
    throw new Error(loginResult?.error || 'Dang nhap that bai');
  }

  if (!loginResult.auth_token) {
    throw new Error('Backend login thanh cong nhung khong tra ve auth_token');
  }

  return {
    auth_token: String(loginResult.auth_token || ''),
    token_type: String(loginResult.token_type || 'Bearer'),
    expires_at: Number(loginResult.expires_at || 0),
    expires_in: Number(loginResult.expires_in || 0),
    nonce_required: loginResult.nonce_required !== false,
    nonce_ttl_seconds: Number(loginResult.nonce_ttl_seconds || 0),
  };
};

const resolveAuthPassword = (data) => {
  const fromPayload =
    data?.password ??
    data?.employee_password ??
    data?.login_password ??
    '';
  if (String(fromPayload || '').trim()) return String(fromPayload || '');

  const fromStorage = getLocalEmployeeSnapshot();
  const fromStoragePassword =
    fromStorage?.password ??
    fromStorage?.employee_password ??
    fromStorage?.login_password ??
    '';
  if (String(fromStoragePassword || '').trim()) {
    return String(fromStoragePassword || '');
  }

  return DEFAULT_EMPLOYEE_LOGIN_PASSWORD;
};

const ensureAttendanceAuth = async (data) => {
  const providedToken = String(data?.auth_token || data?.access_token || data?.token || '').trim();
  const providedNonce = String(data?.nonce || data?.request_nonce || '').trim();
  let employeeCode = resolveEmployeeCodeForAuth(data);
  if (providedToken) {
    return {
      auth_token: providedToken,
      nonce: providedNonce || generateNonce(),
      employee_code: employeeCode || '',
    };
  }

  if (!employeeCode) {
    employeeCode = await resolveEmployeeCodeFromEmployees(data);
  }

  if (!employeeCode) {
    throw new Error('Khong xac dinh duoc employee_code de cap auth_token');
  }

  const cachedSession = getAuthSessionByCode(employeeCode);
  if (isAuthSessionUsable(cachedSession)) {
    return {
      auth_token: cachedSession.auth_token,
      nonce: generateNonce(),
      employee_code: employeeCode,
    };
  }

  clearAuthSessionByCode(employeeCode);
  const password = resolveAuthPassword(data);

  try {
    const freshSession = await requestLoginAuthSession(employeeCode, password);
    setAuthSessionByCode(employeeCode, freshSession);
    return {
      auth_token: freshSession.auth_token,
      nonce: generateNonce(),
      employee_code: employeeCode,
    };
  } catch (error) {
    const details = error?.message ? ` (${error.message})` : '';
    throw new Error(`Khong lay duoc auth token cho ${employeeCode}${details}`);
  }
};

const AUTH_RETRYABLE_CODES = {
  AUTH_REQUIRED: true,
  TOKEN_EXPIRED: true,
  INVALID_TOKEN_FORMAT: true,
  INVALID_TOKEN_SIGNATURE: true,
  INVALID_TOKEN_PAYLOAD: true,
  INVALID_TOKEN_EXP: true,
  INVALID_TOKEN_IAT: true,
  TOKEN_EMPLOYEE_MISMATCH: true,
  NONCE_REQUIRED: true,
  INVALID_NONCE: true,
  NONCE_REPLAY: true,
};

const shouldRetryWithFreshAuth = (result) => {
  if (!result || result.ok !== false) return false;

  const code = String(result.code || '').trim().toUpperCase();
  if (code && AUTH_RETRYABLE_CODES[code]) return true;

  const errText = String(result.error || '').toLowerCase();
  if (!errText) return false;
  return errText.includes('auth_token') || errText.includes('nonce');
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

const fileToBase64WithRetry = async (file, maxRetries = 3) => {
  let lastError;
  for (let i = 0; i < maxRetries; i += 1) {
    try {
      return await fileToBase64(file);
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, 100 * Math.pow(2, i)));
      }
    }
  }
  throw lastError;
};

const generateRequestId = (type, employeeId) => {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now()}_${employeeId || 'emp'}_${type || 'attendance'}_${rand}`;
};

const collectDeviceMeta = () => {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return {};
    }

    const screenWidth = window.screen && window.screen.width ? window.screen.width : window.innerWidth || '';
    const screenHeight = window.screen && window.screen.height ? window.screen.height : window.innerHeight || '';
    const uaData = navigator.userAgentData || null;
    const uaDataMobile = uaData && typeof uaData.mobile === 'boolean' ? uaData.mobile : '';
    const uaDataPlatform = uaData && uaData.platform ? uaData.platform : '';

    return {
      device_type: getDeviceType(),
      user_agent: navigator.userAgent || '',
      platform: navigator.platform || '',
      webdriver: !!navigator.webdriver,
      ua_data_mobile: uaDataMobile,
      ua_data_platform: uaDataPlatform,
      max_touch_points: navigator.maxTouchPoints || 0,
      screen_width: screenWidth,
      screen_height: screenHeight,
      viewport_width: window.innerWidth || '',
      viewport_height: window.innerHeight || '',
    };
  } catch (error) {
    logDebug('[DeviceMeta] collect error:', error);
    return {};
  }
};

export const submitAttendance = async (data) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chua duoc cau hinh. Vui long set REACT_APP_GOOGLE_SCRIPT_URL trong .env');
  }

  try {
    let imageBase64 = null;
    if (data.image) {
      imageBase64 = await fileToBase64WithRetry(data.image);
    }

    const buildParams = (authInfo) => {
      const params = new URLSearchParams();
      const deviceMeta = collectDeviceMeta();
      const clientChannel = resolveAttendanceClientChannel(deviceMeta.device_type);
      const clientOrigin = (() => {
        if (typeof window === 'undefined' || !window.location) return '';
        return window.location.origin || '';
      })();

      params.append('action', 'attendance');
      params.append('employee_id', data.employee_id ?? '');
      params.append('employee_code', data.employee_code ?? '');
      params.append('employee_name', data.employee_name ?? '');
      params.append('type', data.type ?? 'checkin');
      params.append('client_channel', clientChannel);
      params.append('client_app', clientChannel);
      if (clientOrigin) params.append('client_origin', clientOrigin);

      const requestId = (data.request_id || data.requestId || '').toString().trim() ||
        generateRequestId(data.type, data.employee_id);
      params.append('request_id', requestId);
      params.append('latitude', data.latitude ?? '');
      params.append('longitude', data.longitude ?? '');
      params.append('accuracy', data.accuracy ?? '');
      params.append('address', data.address ?? '');

      if (data.explanation) params.append('explanation', data.explanation);
      if (data.business_report) params.append('business_report', data.business_report);
      if (data.work_status_final) params.append('work_status_final', data.work_status_final);
      if (data.office_work_report) params.append('office_work_report', data.office_work_report);
      if (data.outside_office_report) params.append('outside_office_report', data.outside_office_report);
      if (data.province_trip_report) params.append('province_trip_report', data.province_trip_report);
      if (data.training_event_report) params.append('training_event_report', data.training_event_report);
      if (data.timestamp_ms !== undefined && data.timestamp_ms !== null && data.timestamp_ms !== '') {
        params.append('timestamp_ms', String(data.timestamp_ms));
      }
      if (data.force_manual_time !== undefined && data.force_manual_time !== null && data.force_manual_time !== '') {
        params.append('force_manual_time', String(data.force_manual_time));
      }
      if (data.project_code) params.append('project_code', data.project_code);
      if (data.project_name) params.append('project_name', data.project_name);
      if (data.vip !== undefined && data.vip !== null && data.vip !== '') {
        params.append('vip', String(data.vip));
      }
      if (authInfo.auth_token) params.append('auth_token', authInfo.auth_token);
      if (authInfo.nonce) params.append('nonce', authInfo.nonce);
      if (imageBase64) params.append('image_base64', imageBase64);

      appendApiKey(params);
      if (deviceMeta.device_type) params.append('device_type', String(deviceMeta.device_type));
      if (deviceMeta.user_agent) params.append('user_agent', String(deviceMeta.user_agent));
      if (deviceMeta.platform) params.append('platform', String(deviceMeta.platform));
      if (deviceMeta.webdriver !== undefined) params.append('webdriver', String(deviceMeta.webdriver));
      if (deviceMeta.ua_data_mobile !== undefined && deviceMeta.ua_data_mobile !== '') params.append('ua_data_mobile', String(deviceMeta.ua_data_mobile));
      if (deviceMeta.ua_data_platform) params.append('ua_data_platform', String(deviceMeta.ua_data_platform));
      if (deviceMeta.max_touch_points !== undefined) params.append('max_touch_points', String(deviceMeta.max_touch_points));
      if (deviceMeta.screen_width !== undefined && deviceMeta.screen_width !== '') params.append('screen_width', String(deviceMeta.screen_width));
      if (deviceMeta.screen_height !== undefined && deviceMeta.screen_height !== '') params.append('screen_height', String(deviceMeta.screen_height));
      if (deviceMeta.viewport_width !== undefined && deviceMeta.viewport_width !== '') params.append('viewport_width', String(deviceMeta.viewport_width));
      if (deviceMeta.viewport_height !== undefined && deviceMeta.viewport_height !== '') params.append('viewport_height', String(deviceMeta.viewport_height));

      return params;
    };

    const postAttendance = async (params) => {
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return await response.json();
      }

      const textResponse = await response.text();
      logDebug('Response text:', textResponse);
      try {
        return JSON.parse(textResponse);
      } catch {
        throw new Error('Server tra ve du lieu khong hop le: ' + textResponse.substring(0, 200));
      }
    };

    let authInfo = await ensureAttendanceAuth(data);
    let result = await postAttendance(buildParams(authInfo));

    if (shouldRetryWithFreshAuth(result)) {
      const codeForClear = normalizeEmployeeCode(
        authInfo?.employee_code || resolveEmployeeCodeForAuth(data)
      );
      if (codeForClear) {
        clearAuthSessionByCode(codeForClear);
      }

      authInfo = await ensureAttendanceAuth(data);
      result = await postAttendance(buildParams(authInfo));
    }

    return result;
  } catch (error) {
    console.error('Error submitting attendance:', error);
    throw error;
  }
};

export const testGoogleScript = async () => {
  if (!GOOGLE_SCRIPT_URL) {
    return { ok: false, error: 'Google Script URL chưa được cấu hình' };
  }

  try {
    const response = await fetch(GOOGLE_SCRIPT_URL);
    const payload = await response.json();
    return { ok: true, data: payload };
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

export const getEmployees = async (forceRefresh = false) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  if (!forceRefresh) {
    const cached = getCached('employees', CACHE_TTL.EMPLOYEES);
    if (cached) {
      logDebug('[Cache] Using cached employees');
      return cached;
    }
  }

  try {
    const params = new URLSearchParams();
    params.append('action', 'employees');
    appendApiKey(params);
    const response = await fetch(`${GOOGLE_SCRIPT_URL}?${params.toString()}`);
    const result = await response.json();
    if (result.ok && result.data) {
      setCached('employees', result.data);
      return result.data;
    }
    throw new Error(result.error || 'Không thể tải danh sách nhân viên');
  } catch (error) {
    console.error('Error loading employees:', error);
    throw error;
  }
};

export const getProjectLocations = async (forceRefresh = false) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  if (!forceRefresh) {
    const cached = getCached('projectLocations', CACHE_TTL.PROJECT_LOCATIONS);
    if (cached) {
      logDebug('[Cache] Using cached project locations');
      return cached;
    }
  }

  try {
    const params = new URLSearchParams();
    params.append('action', 'projectLocations');
    params.append('t', String(Date.now()));
    appendApiKey(params);
    const url = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;
    const response = await fetch(url, { method: 'GET', cache: 'no-store' });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.ok && result.data) {
      setCached('projectLocations', result.data);
      return result.data;
    }

    console.warn('No project locations found or error:', result.error);
    return [];
  } catch (error) {
    console.error('Error loading project locations:', error);
    return [];
  }
};

const normalizeEmployeeIdentifier = (employeeInput, fallbackId = null) => {
  const fromStorage = (() => {
    try {
      const raw = localStorage.getItem('chamcong_employee');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const readCode = (obj) =>
    String(
      (obj && (
        obj.code ||
        obj.employee_code ||
        obj.employeeCode ||
        obj.Code ||
        obj.CODE
      )) || ''
    ).trim();

  const readId = (obj) =>
    obj
      ? (obj.id ?? obj.employee_id ?? obj.employeeId ?? obj.ID ?? obj.Id ?? null)
      : null;

  if (employeeInput && typeof employeeInput === 'object') {
    const code = readCode(employeeInput) || readCode(fromStorage);
    const id = readId(employeeInput) ?? readId(fromStorage) ?? fallbackId ?? null;
    return { code, id };
  }

  const code = String(employeeInput || '').trim() || readCode(fromStorage);
  const id = readId(fromStorage) ?? fallbackId ?? null;
  return { code, id };
};

export const getTodayAttendance = async (employeeInput, fallbackId = null, forceRefresh = false) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  const { code: employeeCode, id: employeeId } = normalizeEmployeeIdentifier(employeeInput, fallbackId);
  if (!employeeCode && (employeeId === null || employeeId === undefined || employeeId === '')) {
    throw new Error('Missing employee identifier');
  }

  const cacheIdentity = employeeCode || String(employeeId);
  const cacheKey = `todayAttendance_${cacheIdentity}`;
  if (!forceRefresh) {
    const cached = getCached(cacheKey, CACHE_TTL.TODAY_ATTENDANCE);
    if (cached !== null) {
      logDebug('[Cache] Using cached today attendance');
      return cached;
    }
  }

  try {
    const params = new URLSearchParams();
    params.append('action', 'todayAttendance');
    appendApiKey(params);
    if (employeeCode) {
      params.append('employee_code', employeeCode);
    }
    if (employeeId !== null && employeeId !== undefined && employeeId !== '') {
      params.append('employee_id', String(employeeId));
    }
    params.append('t', String(Date.now()));
    const url = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;
    logDebug('Fetching today attendance from:', url);

    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    logDebug('Today attendance response:', result);
    if (result.ok) {
      const payload = result.data;
      setCached(cacheKey, payload);
      return payload;
    }

    throw new Error(result.error || 'Không thể tải chấm công hôm nay');
  } catch (error) {
    console.error('Error loading today attendance:', error);
    return null;
  }
};

export const getAttendanceHistory = async (employeeInput, startDate = null, endDate = null, fallbackId = null) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  const { code: employeeCode, id: employeeId } = normalizeEmployeeIdentifier(employeeInput, fallbackId);
  if (!employeeCode && (employeeId === null || employeeId === undefined || employeeId === '')) {
    throw new Error('Missing employee identifier');
  }

  try {
    const params = new URLSearchParams();
    params.append('action', 'attendance');
    appendApiKey(params);
    if (employeeCode) {
      params.append('employee_code', employeeCode);
    }
    if (employeeId !== null && employeeId !== undefined && employeeId !== '') {
      params.append('employee_id', String(employeeId));
    }
    params.append('t', String(Date.now()));
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const url = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;

    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.ok) {
      return result.data || [];
    }

    console.error('API returned error:', result.error);
    return [];
  } catch (error) {
    console.error('Error loading attendance history:', error);
    return [];
  }
};
